<template>
    <modal size="lg" @close="reset(); $emit('close')" :height-max="true" ref="modal" :is-closeable="isCloseable" :autoshow="true">
        <template v-slot:title>
            <h3 class="modal-title">Add Insurance Plan</h3>
        </template>

        <slot>
            <div v-show="step == 'plan'">
                <insurance-plan-form
                    ref="planForm"
                    :deduction-types="deductionTypes"
                    :initial-value="formData.plan"
                />
            </div>

            <div v-show="step == 'costs'">
                <flat-pricing-form
                    ref="flatCostsForm"
                    v-show="formData.plan.billing_type == 'flat'"
                    :initial-value="formData.flat_pricing"
                    :employee-cost-covered="formData.plan.employee_cost_covered"
                    :dependent-cost-covered="formData.plan.dependent_cost_covered"
                />

                <age-band-pricing-form
                    ref="ageBandCostsForm"
                    v-show="formData.plan.billing_type == 'age-band'"
                    :initial-value="formData.age_band_pricing"
                    :employee-cost-covered="formData.plan.employee_cost_covered"
                />
            </div>

            <div v-show="step == 'plan-numbers'">
                <plan-numbers-form
                    ref="planNumbersForm"
                    :initial-value="formData.plan_numbers"
                    :client="client"
                />
            </div>

            <div v-if="step == 'positions' && positionsLoaded">
                <position-selector
                    :all-positions="allCompanyPositions"
                    :companies="client.companies"
                    v-model="formData.positions"
                />
            </div>
        </slot>

        <template v-slot:footer>
            <form-errors :errors="formErrors"/>

            <button-row stacked>
                <button v-if="step == 'plan'" type="button" class="btn btn-outline-primary" @click.prevent="close()">
                    Cancel
                </button>
                <button v-if="['costs', 'plan-numbers', 'positions'].includes(step)" type="button" class="btn btn-outline-primary" @click.prevent="previousStep">
                    Back
                </button>
                <button v-if="['plan', 'costs', 'plan-numbers'].includes(step)" type="button" class="btn btn-primary" @click.prevent="nextStep">
                    Next
                </button>

                <button v-if="step == 'positions'" type="button" class="btn btn-primary" @click.prevent="onSubmit()">
                    Save
                </button>
            </button-row>
        </template>
    </modal>
</template>

<script>
import FormMixin from '@/mixins/Form'
import ModalMixin from '@/mixins/ModalMixin'
import InsurancePlanForm from './InsurancePlanForm'
import FlatPricingForm from './FlatPricingForm'
import AgeBandPricingForm from './AgeBandPricingForm'
import PlanNumbersForm from './PlanNumbersForm'
import PositionSelector from '@/pages/client/Settings/components/PositionSelector'

export default {
    props: ['client', 'deductionTypes', ],
    mixins: [FormMixin, ModalMixin],
    components: {InsurancePlanForm, FlatPricingForm, AgeBandPricingForm, PlanNumbersForm, PositionSelector, },
    emits: ['updated', 'close', ],
    mounted() {
        this.getPositions()
    },
    data() {
        return {
            formData: this.makeFormData(),
            step: 'plan',
            positionsLoaded: false,
            allPositions: [],
        }
    },
    computed: {
        allCompanyPositions() {
            const companies = Array.from(Object.keys(this.formData.plan_numbers))
            return this.allPositions.filter(p => companies.includes('' + p.company_id))
        },
    },
    methods: {
        makeFormData() {
            return {
                plan: {
                    plan_name: '',
                    plan_type: '',
                    deduction_type: '',
                    starts_on: '',
                    open_enrollment_starts_on: '',
                    waiting_period: '',
                    termination_mode: '',
                    billing_type: '',
                    age_determination_method: '',

                    employee_cost_covered: '',
                    dependent_cost_covered: '',
                },
                flat_pricing: {
                    single_total_cost: '',

                    single_plus_one_total_cost: '',
                    single_plus_children_total_cost: '',
                    family_total_cost: '',

                    has_single_plus_one: false,
                    has_single_plus_children: false,
                    has_family: false,
                },
                age_band_pricing: {},
                plan_numbers: {},
                positions: [],
            }
        },
        getPositions() {
            this.$api.get(`/clients/${this.client.id}/positions`).then(resp => {
                this.allPositions = resp
                this.allPositions.sort((a, b) => {return a.terminal_value.toLowerCase() > b.terminal_value.toLowerCase() ? 1 : -1})
                this.positionsLoaded = true
            }).catch((errors) => {
                this.$bus.showError(errors.__all__)
            })
        },
        previousStep() {
            if (this.step == 'costs') {
                this.step = 'plan'
            }
            else if (this.step == 'plan-numbers') {
                this.step = 'costs'
            }
            else if (this.step == 'positions') {
                this.step = 'plan-numbers'
            }

        },
        nextStep() {
            this.formErrors = []

            if (this.step == 'plan') {
                if (!this.$refs.planForm.validate()) {
                    this.formErrors = this.$refs.planForm.formErrors
                    return
                }

                this.formData.plan = this.$refs.planForm.getFormData()
                this.step = 'costs'
            }

            else if (this.step == 'costs') {
                if (this.formData.plan.billing_type == 'flat') {
                    if (!this.$refs.flatCostsForm.validate()) {
                        this.formErrors = this.$refs.flatCostsForm.formErrors
                        return
                    }

                    this.formData.flat_pricing = this.$refs.flatCostsForm.getFormData()
                    this.formData.age_band_pricing = {}
                    this.step = 'plan-numbers'
                }
                else if (this.formData.plan.billing_type == 'age-band') {
                    if (!this.$refs.ageBandCostsForm.validate()) {
                        this.formErrors = this.$refs.ageBandCostsForm.formErrors
                        return
                    }

                    this.formData.age_band_pricing = this.$refs.ageBandCostsForm.getFormData()
                    this.formData.flat_pricing = {}
                    this.step = 'plan-numbers'
                }
            }
            else if (this.step == 'plan-numbers') {
                if (!this.$refs.planNumbersForm.validate()) {
                    this.formErrors = this.$refs.planNumbersForm.formErrors
                    return
                }

                this.formData.plan_numbers = this.$refs.planNumbersForm.getPlanNumbers()  // NB this does use getPlanNumbers() and not getFormData()
                this.step = 'positions'
            }
        },
        onSubmit() {
            if (!this.validate()) {
                return
            }

            const data = this.getFormData()
            this.$store.dispatch('START_LOADING')
            const url = `/clients/${this.client.id}/settings/insurance-plans`
            this.$api.post(url, data).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showInfo(`Successfully added ${Object.keys(data.plan_numbers).length} plans.`)
                this.$emit('updated')
                this.close()
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.formErrors = (errors.__all__ || [])
                this.$bus.showError(errors.__all__)
            })
        }
    }
}
</script>
